import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { 
  MapPin, Building2, Music, Mountain, Search, X, Phone, 
  ExternalLink, Navigation, Layers, ZoomIn, ZoomOut, Locate,
  Globe, ChevronRight, Clock, Star, List, Grid, Map, Filter,
  Compass, Share2, Heart, MessageCircle
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";
import "leaflet/dist/leaflet.css";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

// Type configuration with enhanced styling
const typeConfig = {
  business: { 
    icon: Building2, 
    color: "#059669", 
    bgColor: "#D1FAE5",
    gradient: "from-emerald-500 to-teal-600",
    label: "Negocios",
    markerColor: "green"
  },
  artist: { 
    icon: Music, 
    color: "#7C3AED", 
    bgColor: "#EDE9FE",
    gradient: "from-violet-500 to-purple-600",
    label: "Artistas",
    markerColor: "violet"
  },
  destination: { 
    icon: Mountain, 
    color: "#D97706", 
    bgColor: "#FEF3C7",
    gradient: "from-amber-500 to-orange-600",
    label: "Destinos",
    markerColor: "gold"
  },
};

// Create custom marker icons using standard Leaflet icons with colors
const createCustomIcon = (type) => {
  const config = typeConfig[type] || typeConfig.business;
  const colorMap = {
    business: 'green',
    artist: 'violet',
    destination: 'gold'
  };
  const color = colorMap[type] || 'green';
  
  return L.icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

// Cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  if (count > 10) size = 'medium';
  if (count > 25) size = 'large';
  
  const sizeMap = { small: 36, medium: 44, large: 52 };
  const iconSize = sizeMap[size];
  
  return L.divIcon({
    html: `
      <div class="flex items-center justify-center w-full h-full rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold shadow-xl border-4 border-white">
        ${count}
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: L.point(iconSize, iconSize, true),
  });
};

// Map center control component
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
}

// Locate me button component
function LocateControl({ onLocate }) {
  const map = useMap();
  
  const handleLocate = () => {
    map.locate({ setView: true, maxZoom: 16 });
    map.on('locationfound', (e) => {
      onLocate && onLocate(e.latlng);
    });
  };
  
  return (
    <button
      onClick={handleLocate}
      className="absolute bottom-20 right-3 z-[1000] w-10 h-10 bg-white rounded-xl shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
      title="Mi ubicación"
    >
      <Locate className="h-5 w-5 text-gray-700" />
    </button>
  );
}

// Location Card Component
function LocationCard({ location, onClose, onNavigate }) {
  const config = typeConfig[location.type] || typeConfig.business;
  const IconComponent = config.icon;
  const image = getImageUrl(location.image_url);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: location.name,
        text: `Descubre ${location.name} en Choluteca.net`,
        url: window.location.origin + location.url,
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 100, scale: 0.9 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className="absolute bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-[400px] bg-white rounded-3xl shadow-2xl overflow-hidden z-[1000] border border-gray-100"
      data-testid="location-card"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-9 h-9 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/40 transition-colors"
      >
        <X className="h-5 w-5 text-white" />
      </button>

      {/* Image with gradient overlay */}
      <div className="relative h-48">
        {image ? (
          <img src={image} alt={location.name} className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <IconComponent className="h-16 w-16 text-white/50" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Badge on image */}
        <div className="absolute bottom-4 left-4">
          <Badge 
            className="px-3 py-1.5 text-xs font-semibold border-0"
            style={{ backgroundColor: config.color, color: 'white' }}
          >
            <IconComponent className="h-3 w-3 mr-1.5" />
            {location.category_name}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-bold text-xl text-gray-900 mb-2 line-clamp-1">
          {location.name}
        </h3>

        {/* Info items */}
        <div className="space-y-2 mb-4">
          {location.address && (
            <div className="flex items-start gap-2.5 text-sm text-gray-600">
              <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{location.address}</span>
            </div>
          )}
          
          {location.phone && (
            <a
              href={`tel:${location.phone}`}
              className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-emerald-600 transition-colors"
            >
              <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span>{location.phone}</span>
            </a>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Link to={location.url} className="flex-1">
            <Button 
              className="w-full rounded-xl h-11 font-semibold"
              style={{ backgroundColor: config.color }}
            >
              Ver detalles
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
          
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="rounded-xl h-11 w-11 p-0">
              <Navigation className="h-5 w-5" />
            </Button>
          </a>
          
          <Button 
            variant="outline" 
            className="rounded-xl h-11 w-11 p-0"
            onClick={handleShare}
          >
            <Share2 className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Sidebar list item
function ListItem({ location, isSelected, onClick }) {
  const config = typeConfig[location.type] || typeConfig.business;
  const IconComponent = config.icon;
  const image = getImageUrl(location.image_url);
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
        isSelected 
          ? 'bg-gradient-to-r from-emerald-50 to-teal-50 ring-2 ring-emerald-500/20' 
          : 'hover:bg-gray-50'
      }`}
    >
      {image ? (
        <img src={image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
      ) : (
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: config.bgColor }}
        >
          <IconComponent className="h-6 w-6" style={{ color: config.color }} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900 truncate">{location.name}</h4>
        <p className="text-sm text-gray-500 truncate">{location.category_name}</p>
        {location.address && (
          <p className="text-xs text-gray-400 truncate mt-0.5">{location.address}</p>
        )}
      </div>
      <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
    </button>
  );
}

export default function MapaPage() {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showList, setShowList] = useState(false);
  const [mapCenter, setMapCenter] = useState([13.3006, -87.1908]);
  const [mapZoom, setMapZoom] = useState(13);
  const mapRef = useRef(null);

  useEffect(() => {
    fetchMapData();
  }, []);

  useEffect(() => {
    filterLocations();
  }, [locations, searchQuery, typeFilter]);

  const fetchMapData = async () => {
    try {
      const [locRes, configRes] = await Promise.all([
        axios.get(`${API}/map/locations`),
        axios.get(`${API}/map/config`)
      ]);
      
      setLocations(locRes.data.locations);
      setMapConfig(configRes.data);
      
      if (locRes.data.center) {
        setMapCenter([locRes.data.center.lat, locRes.data.center.lng]);
      }
    } catch (error) {
      console.error("Error fetching map data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterLocations = () => {
    let filtered = [...locations];
    
    if (typeFilter !== "all") {
      filtered = filtered.filter(loc => loc.type === typeFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(loc => 
        loc.name.toLowerCase().includes(query) ||
        loc.category_name?.toLowerCase().includes(query) ||
        loc.address?.toLowerCase().includes(query)
      );
    }
    
    setFilteredLocations(filtered);
  };

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    setMapCenter([location.lat, location.lng]);
    setMapZoom(16);
  };

  const handleListItemClick = (location) => {
    setSelectedLocation(location);
    setMapCenter([location.lat, location.lng]);
    setMapZoom(16);
    setShowList(false);
  };

  const stats = mapConfig?.stats || { businesses: 0, artists: 0, destinations: 0 };

  return (
    <Layout>
      {/* Compact Hero Header */}
      <section className="relative bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-900 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-emerald-300 text-sm font-medium mb-4">
              <Compass className="h-4 w-4" />
              Explora la región
              <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full ml-1">v3.0</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
              Mapa Interactivo
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Descubre negocios, artistas y destinos turísticos de Choluteca
            </p>
            
            {/* Stats pills */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {Object.entries(typeConfig).map(([key, config]) => {
                const IconComponent = config.icon;
                const count = key === 'business' ? stats.businesses : 
                              key === 'artist' ? stats.artists : stats.destinations;
                return (
                  <div 
                    key={key}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full"
                  >
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: config.color }}
                    >
                      <IconComponent className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-white font-medium">{count}</span>
                    <span className="text-white/60 text-sm">{config.label}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Search and Filters Bar */}
      <section className="sticky top-0 z-[500] bg-white/95 backdrop-blur-md border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col md:flex-row gap-3 items-center">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Buscar lugares..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 h-11 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="map-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Type filters */}
            <div className="flex flex-wrap gap-2 items-center justify-center md:justify-start flex-1">
              <button
                onClick={() => setTypeFilter("all")}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
                  typeFilter === "all"
                    ? "bg-gray-900 text-white shadow-lg"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Layers className="h-4 w-4" />
                Todos
                <span className="text-xs opacity-70">({locations.length})</span>
              </button>
              
              {Object.entries(typeConfig).map(([key, config]) => {
                const IconComponent = config.icon;
                const count = locations.filter(l => l.type === key).length;
                return (
                  <button
                    key={key}
                    onClick={() => setTypeFilter(key)}
                    className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
                      typeFilter === key
                        ? "text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    style={typeFilter === key ? { backgroundColor: config.color } : {}}
                  >
                    <IconComponent className="h-4 w-4" />
                    {config.label}
                    <span className="text-xs opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={showList ? "outline" : "default"}
                size="icon"
                className="rounded-xl h-11 w-11"
                onClick={() => setShowList(false)}
              >
                <Map className="h-5 w-5" />
              </Button>
              <Button
                variant={showList ? "default" : "outline"}
                size="icon"
                className="rounded-xl h-11 w-11"
                onClick={() => setShowList(true)}
              >
                <List className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative" style={{ height: "calc(100vh - 280px)", minHeight: "500px" }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Cargando mapa...</p>
            </div>
          </div>
        ) : (
          <div className="flex h-full">
            {/* Sidebar List */}
            <AnimatePresence>
              {showList && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 400, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ type: "spring", damping: 25 }}
                  className="h-full bg-white border-r overflow-hidden hidden md:block"
                >
                  <ScrollArea className="h-full">
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center justify-between">
                        <span>Lugares ({filteredLocations.length})</span>
                        <Badge variant="secondary">{typeFilter === 'all' ? 'Todos' : typeConfig[typeFilter]?.label}</Badge>
                      </h3>
                      <div className="space-y-2">
                        {filteredLocations.map((location) => (
                          <ListItem
                            key={`${location.type}-${location.id}`}
                            location={location}
                            isSelected={selectedLocation?.id === location.id}
                            onClick={() => handleListItemClick(location)}
                          />
                        ))}
                        {filteredLocations.length === 0 && (
                          <div className="text-center py-12">
                            <MapPin className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                            <p className="text-gray-500">No se encontraron lugares</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Map Container */}
            <div className="flex-1 relative">
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                ref={mapRef}
                className="h-full w-full"
                zoomControl={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                <MapController center={mapCenter} zoom={mapZoom} />
                <LocateControl />
                
                {/* Zoom controls */}
                <div className="absolute top-4 right-3 z-[1000] flex flex-col gap-2">
                  <ZoomControl position="topright" />
                </div>
                
                {/* Clustered markers */}
                <MarkerClusterGroup
                  chunkedLoading
                  iconCreateFunction={createClusterCustomIcon}
                  maxClusterRadius={60}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                >
                  {filteredLocations.map((location) => (
                    <Marker
                      key={`${location.type}-${location.id}`}
                      position={[location.lat, location.lng]}
                      icon={createCustomIcon(location.type)}
                      eventHandlers={{
                        click: () => handleMarkerClick(location),
                      }}
                    />
                  ))}
                </MarkerClusterGroup>
              </MapContainer>

              {/* Legend */}
              <div className="absolute bottom-6 left-4 z-[1000]">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-gray-100">
                  <h4 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-emerald-600" />
                    Leyenda
                  </h4>
                  <div className="space-y-2.5">
                    {Object.entries(typeConfig).map(([key, config]) => {
                      const IconComponent = config.icon;
                      const count = filteredLocations.filter(l => l.type === key).length;
                      return (
                        <div key={key} className="flex items-center gap-3 text-sm">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: config.bgColor }}
                          >
                            <IconComponent className="h-4 w-4" style={{ color: config.color }} />
                          </div>
                          <span className="text-gray-700 font-medium">{config.label}</span>
                          <span 
                            className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: config.bgColor, color: config.color }}
                          >
                            {count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Selected Location Card */}
              <AnimatePresence>
                {selectedLocation && (
                  <LocationCard
                    location={selectedLocation}
                    onClose={() => setSelectedLocation(null)}
                  />
                )}
              </AnimatePresence>

              {/* Empty state */}
              {filteredLocations.length === 0 && !loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-[900]">
                  <div className="text-center px-4">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MapPin className="h-10 w-10 text-gray-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      No hay ubicaciones
                    </h3>
                    <p className="text-gray-500 max-w-md">
                      {searchQuery || typeFilter !== "all"
                        ? "No se encontraron lugares con los filtros seleccionados"
                        : "Aún no hay lugares geolocalizados"}
                    </p>
                    {(searchQuery || typeFilter !== "all") && (
                      <Button 
                        onClick={() => { setSearchQuery(""); setTypeFilter("all"); }}
                        className="mt-4 rounded-xl"
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Mobile List Sheet */}
      {showList && (
        <Sheet open={showList} onOpenChange={setShowList}>
          <SheetContent side="bottom" className="h-[70vh] rounded-t-3xl md:hidden">
            <SheetHeader>
              <SheetTitle>Lugares ({filteredLocations.length})</SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-full mt-4">
              <div className="space-y-2 pb-8">
                {filteredLocations.map((location) => (
                  <ListItem
                    key={`${location.type}-${location.id}`}
                    location={location}
                    isSelected={selectedLocation?.id === location.id}
                    onClick={() => handleListItemClick(location)}
                  />
                ))}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>
      )}

      {/* Custom styles for cluster icons */}
      <style>{`
        .custom-marker {
          background: transparent;
          border: none;
        }
        .custom-cluster-icon {
          background: transparent;
        }
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06) !important;
        }
        .leaflet-control-zoom a {
          background: white !important;
          color: #374151 !important;
          border: none !important;
          width: 40px !important;
          height: 40px !important;
          line-height: 40px !important;
          font-size: 18px !important;
          border-radius: 12px !important;
          margin-bottom: 8px !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f3f4f6 !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
        }
      `}</style>
    </Layout>
  );
}
